import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useIntl } from 'react-intl';
import {
  Button,
  Form,
  Input,
  Label,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from 'reactstrap';
import _ from 'lodash';
import ErrorBoundary from '../ErrorBoundary';
import ModalTabHeader from '../ModalTabHeader';
import DownloadReasonSelector from '../Download/ReasonsSelector';

import { APIcreateDownload } from '../../api/Downloads';

import { findPathToKey } from '../../helpers/App';
import {
  getDownloadReasons,
  shouldShowDownloadReasons,
  translateAnswerTree,
} from '../../helpers/Download';

const ImageEditorDownloadModal = ({
  file,
  fileExtensions,
  downloadCallback,
  prepareDownload,
  handleClose,
}) => {
  const intl = useIntl();

  const shouldShowReason = shouldShowDownloadReasons(null, [file]);

  const [activeTab, setActiveTab] = useState(shouldShowReason ? 'reason' : 'default');
  const [selectedFileFormats, setSelectedFileFormats] = useState([]);
  const [answerTree, setAnswerTree] = useState({});
  const [showAnswerTreeErrors, setShowAnswerTreeErrors] = useState(false);

  const validateReason = () => {
    setShowAnswerTreeErrors(true);

    const errorPath = findPathToKey(answerTree, 'requiredError');

    if (errorPath && _.get(answerTree, errorPath)) {
      // scrollModalToError('modal-download');
      return;
    }

    setActiveTab('default');
  };

  const handleChangeFileFormats = (e) => {
    const { value, checked } = e.target;

    setSelectedFileFormats(prev => (checked
      ? [...prev, value]
      : prev.filter(f => f !== value)));
  };

  const handleDownload = () => {
    const translatedAnswerTree = shouldShowReason ? translateAnswerTree(answerTree) : null;

    prepareDownload(
      [file],
      [],
      [],
      selectedFileFormats,
      [],
      null,
      translatedAnswerTree,
      { image_editor: true },
    ).then(() => {
      downloadCallback(selectedFileFormats);
      handleClose();
    });
  };

  const reasons = getDownloadReasons(null);

  return (
    <ErrorBoundary>
      <ModalHeader className="modal-header-center">
        {intl.formatMessage({ defaultMessage: 'Download' })}
      </ModalHeader>
      <ModalTabHeader
        show={shouldShowReason}
        tabs={[
          { index: 'reason', label: intl.formatMessage({ defaultMessage: 'File usage' }) },
          { index: 'default', label: intl.formatMessage({ defaultMessage: 'Format' }) },
        ]}
        activeTab={activeTab}
      />
      {(activeTab === 'reason') && (
        <ModalBody>
          <Form className="reason-form">
            <DownloadReasonSelector
              reasons={reasons}
              answerTree={answerTree}
              handleAnswerTree={setAnswerTree}
              showAnswerTreeErrors={showAnswerTreeErrors}
            />
          </Form>
        </ModalBody>
      )}
      {(activeTab === 'default') && (
        <ModalBody>
          <ul className="file-sizes-list">
            {fileExtensions.map(extension => (
              <li
                key={extension.key}
              >
                <div className="checkbox-wrapper">
                  <Input
                    type="checkbox"
                    className="input-check check-l"
                    id={`file-format-option-${extension.key}`}
                    value={extension.key}
                    checked={selectedFileFormats.includes(extension.key)}
                    onChange={handleChangeFileFormats}
                  />
                </div>
                <div className="checkbox-label">
                  <Label
                    htmlFor={`file-format-option-${extension.key}`}
                  >
                    {extension.label}
                  </Label>
                </div>
              </li>
            ))}
          </ul>
        </ModalBody>
      )}
      <ModalFooter>
        <Button
          color="link"
          onClick={handleClose}
        >
          {intl.formatMessage({ defaultMessage: 'Cancel' })}
        </Button>
        {activeTab === 'reason' && (
          <Button
            color="primary"
            onClick={validateReason}
          >
            {intl.formatMessage({ defaultMessage: 'Continue' })}
          </Button>
        )}
        {activeTab === 'default' && (
          <Button
            color="primary"
            onClick={handleDownload}
            disabled={selectedFileFormats.length === 0}
          >
            {intl.formatMessage({ defaultMessage: 'Download' })}
          </Button>
        )}
      </ModalFooter>
    </ErrorBoundary>
  );
};

const mapStateToProps = state => ({
  clientSettings: state.App.settings,
});

const mapDispatchToProps = () => ({
  prepareDownload: (
    files,
    collections,
    fileSizes,
    fileFormats,
    versions,
    requestUuid = null,
    reason = null,
    downloadAttributes = null,
  ) => (
    APIcreateDownload({
      files: files.map(f => (f.uuid)),
      collections,
      file_sizes: fileSizes,
      file_formats: fileFormats,
      request_uuid: requestUuid,
      versions,
      reason,
      download_attributes: downloadAttributes,
    })
  ),
});

ImageEditorDownloadModal.propTypes = {
  file: PropTypes.object.isRequired,
  fileExtensions: PropTypes.array.isRequired,
  downloadCallback: PropTypes.func.isRequired,
  prepareDownload: PropTypes.func.isRequired,
  handleClose: PropTypes.func.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(ImageEditorDownloadModal);
